import { gql } from '@apollo/client';
import { insuranceFragment } from 'store/fragments';

export default gql`
  query booking($reference: String!) {
    booking(reference: $reference) {
      detailsCaptured
      isHomogeneous
      salesChannelCode
      totalPriceToBePaid
      createTimestamp
      confirmedTimestamp
      cancelledTimestamp
      vouchers {
        id
        code
        description
        class
        paymentId
        isActive
      }
      latestRevisionSource {
        username
      }
      agent {
        contractCode
      }
      isLoyalty
      isPackage
      isGroupBooking
      productFamily
      productFamilyGroup
      isDOBRequired
      additionalDetails {
        value
        key
        itemRef
        passengerRef
      }
      notes {
        content
        author
        createdAt
      }
      insurances {
        ...CompleteInsurance
      }
      nonExchangedOutbound: outbound(hideExchanged: true) {
        id
      }
      nonExchangedInbound: inbound(hideExchanged: true) {
        id
      }
      payments {
        id
        refId
        currency
        currencyRate
        reference
        externalReference
        refundAvailable
        provider
        method
        amount
        amountWithCurrency
        availableRefundAmount
        transactionTimestamp
        refundProcessTimestamp
        paymentStatus
        isPayout
        isBalance
        isSuccessful
        createdRevision
      }
      language
      reference
      status
      isInAfterSales
      displayStatus @client
      arePassengersEditable @client
      allBookingItems @client {
        ...product
        status
        isPrrAttempted
      }
      eurostarBookingItems @client {
        ...product
        status
        isPrrAttempted
      }
      greenspeedBookingItems @client {
        ...product
        status
        isPrrAttempted
      }
      expiryTimestamp
      changeExpiryTimestamp
      isInAfterSales
      passengers {
        ...passenger
      }
      customer {
        firstName
        lastName
        email
        phoneNumber
        unformattedPhoneNumber {
          countryCode
          number
        }
        address {
          street
          postalCode
          city
          countryCode
        }
      }
      currency
      productFamilies {
        code
        name
        familyGroup
      }
      totalPrice
      outbound(hideExchanged: true, sortByDepartureDate: true) {
        ...leg
      }
      inbound(hideExchanged: true, sortByDepartureDate: true) {
        ...leg
      }
      outboundCancelled: outbound(includeCancelledProvisional: true, sortByDepartureDate: true) {
        ...leg
      }
      inboundCancelled: inbound(includeCancelledProvisional: true, sortByDepartureDate: true) {
        ...leg
      }
      outboundExtras {
        ...product
      }
      inboundExtras {
        ...product
      }
      outboundWithExchangedProducts: outbound(hideExchanged: false) {
        trainNumber
        classOfService {
          code
        }
        timing {
          isoDate
        }
        products {
          itemRef
          associatedItemRefs
          cancellationReason
          cancelledRevision
          createdRevision
          isCancelled
          isActive
          passengerId
          provisional
        }
      }
      inboundWithExchangedProducts: inbound(hideExchanged: false) {
        trainNumber
        classOfService {
          code
        }
        timing {
          isoDate
        }
        products {
          itemRef
          associatedItemRefs
          cancellationReason
          cancelledRevision
          createdRevision
          isCancelled
          isActive
          passengerId
          provisional
        }
      }
      pendingCreationLegs {
        ...leg
      }
      isMigratedMulti
      agent {
        organization
        office
        username
      }
      hotels {
        price
        jacTravelRef
        isDotwBooking
        cancelled
        provisional
        numberOfNights
        guestCount
        checkInDate
      }
      externalBookingReferences {
        source
        externalId
      }
      wasPurchasedWithSubscription
      onHold {
        depositAmount
      }
    }
  }

  fragment leg on Leg {
    ...legFields
    addOnLeg {
      ...legFields
    }
  }

  fragment legFields on Leg {
    uuid
    cancelled
    extraProducts {
      itemRef
      code
      name
      price
      priceWithCurrency
      currency
      isCancelled
      isPendingCancellation
      passengerId
      type
      passenger {
        id
        uuid
        type
        firstName
        lastName
        email
        phoneNumber
        unformattedPhoneNumber {
          countryCode
          number
        }
        birthDate
        loyaltyNumber
        disabilityType
      }
      tariffDetails {
        tariffCode
        bucketCode
      }
    }
    isMultiInventory
    isThroughFare
    isSummatedFare
    isSplitPassengerEnabled
    ticketDocuments {
      pin
      segmentId
      passengerId
    }
    serviceType {
      code
      name
      brandCode
    }
    serviceIdentifier
    direction
    trainNumber
    id
    classOfService {
      code
      name
    }
    origin {
      name
      uic
      shortCode
      timezone
      isPseudoStation
    }
    destination {
      name
      uic
      shortCode
    }
    timing {
      isoDate
      date
      departs
      arrives
    }
    fees {
      name
      code
      price
      provisional
      currency
    }
    canBeUpgraded
    products {
      ...product
    }
    activeBookingItems @client {
      ...product
      isPrrAttempted
    }
    bookingItems @client {
      ...product
      mealCode
      status
      refundMethod
      isPrrAttempted
    }

    temporarilyCancelledBookingItems @client {
      ...product
      mealCode
      status
    }

    meals {
      code
      passenger {
        id
      }
      price
      name
    }
    productSummaries @client {
      name
      passengerType
      code
      total
      quantity
      price
    }
    refundSummaries @client {
      name
      passengerType
      code
      total
      quantity
      price
    }
  }

  fragment passenger on Passenger {
    id
    uuid
    type
    firstName
    lastName
    email
    phoneNumber
    unformattedPhoneNumber {
      countryCode
      number
    }
    birthDate
    loyaltyNumber
    disabilityType
    discountCards {
      code
      identifier
      name
      requiresPassengerIdentifier
    }
    travelDocumentsRequired {
      travelDocumentType
      required
    }
  }

  fragment product on Product {
    uuid
    itemId
    externalTicketNumber
    externalProductReference
    itemRef
    currency
    loyaltyPoints
    associatedItemRefs
    passengerId
    isAFareAddOn
    isCheckedIn
    isCancelled
    isSuspended
    isPlaceholder
    isExpired
    externalTicketNumber
    isActive
    isPrrRefunded
    isRebookOFEActive
    isCancelOFEActive
    isUKEntryDenied
    price
    ticketNumber
    voidTimestamp
    expiryTimestamp
    type
    tariffDetails {
      tariffCode
      bucketCode
      mandatoryReturn
    }
    confirmedTimestamp
    code
    inventoryClass
    name
    cancelled
    provisional
    canBeRebooked
    canBeCancelled
    canBeRefunded
    canBeUpgrade
    canChangeSeat
    canChangeName
    productFamily {
      code
      familyGroup
      name
      familyGroupName
    }
    afterSalesRules {
      active
      validUntil
      price
      override
      type
      currentActive
      reasonCode
    }
    afterSalesCurrentOverrides: afterSalesRules(filterCurrentOverrides: true) {
      type
      validUntil
      reasonCode
    }
    seat {
      carriage
      number
    }
    passenger {
      ...passenger
    }
    originalPrice
    discounts {
      voucherId
    }
    createdRevision
    cancelledRevision
    cancellationReason
    additionalDetails {
      key
      value
    }
  }
  ${insuranceFragment}
`;
